import { useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { Any, ApiPermissions } from "@cede/types";
import {
  buildStringWithRequirements,
  checkRequiredPermissionStatement,
  extractPages,
  hasPermissionsWithProxy,
  hasPermissionsWithoutProxy,
  makeFirstLettersCapital,
  redirectInBrowser,
} from "@cede/utils";
import { AddressDescription } from "../AddressDescription";
import { CopyIconButton } from "../CopyIconButton";
import { ExternalLink } from "../ExternalLink";
import { TutorialAffiliation } from "../TutorialAffiliation/TutorialAffiliation";
import { TutorialBox } from "../TutorialBox/TutorialBox";
import { TutorialInfo } from "../TutorialInfo/TutorialInfo";
import { TutorialList } from "../TutorialList/TutorialList";
import { TutorialVideoCard } from "../TutorialVideoCard/TutorialVideoCard";
import { ActionProps, TutorialReaderProps } from "./types";
import { ExtensionImage } from "@/components/shared/ExtensionImage/ExtensionImage";
import { ExtensionImageProps } from "@/components/shared/ExtensionImage/types";
import { SpinnerLoader } from "@/components/shared/Loaders/Loader";
import { useSupportedExchanges } from "@/hooks/core/useSupportedExchanges";
import { cn } from "@/lib/utils";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle, XCircle } from "lucide-react";
import rehypeRaw from "rehype-raw";

const DEFAULT_DATA = {};
const DEFAULT_ACTION_PROPS = {};
const REHYPE_PLUGINS = [rehypeRaw] as Any[];

const WrappedExtensionImage = (
  props: ExtensionImageProps & { type?: string; data: Record<string, string | number>; width?: string },
) => {
  if (props.type && props.type === "allowedValueToSend") {
    return (
      <div className="relative">
        <p className="z-top absolute bottom-[25%] left-[15%] text-black text-sm">{props.data["allowedValueToSend"]}</p>
        <ExtensionImage {...props} />
      </div>
    );
  }

  return <ExtensionImage {...props} />;
};

// Render children as a string or as a component
function renderChildren(children: Any, wrapperClass?: string) {
  if (!Array.isArray(children)) {
    children = [children];
  }
  return children.map((child: Any, index: number) => {
    if (typeof child === "string") {
      const normalized = child.replace(/\s+/g, " ").trim();
      if (!normalized) {
        return null;
      }
      return (
        <span key={child.toString() + index} className={wrapperClass}>
          {normalized}
        </span>
      );
    }

    return (
      <span key={index} className={wrapperClass}>
        {child}
      </span>
    );
  });
}

function renderChildrenAsString(children: Any) {
  if (!Array.isArray(children)) {
    return children;
  }
  return children.join("");
}

function divTransformer(componentType: string, properties: Record<string, Any>, permissions: ApiPermissions[]) {
  // Check the permissions of the current process to display the section or not
  if (properties.require && !checkRequiredPermissionStatement(properties.require, permissions)) return null;
  switch (componentType) {
    case "section": {
      const title = buildStringWithRequirements(properties.title, permissions);
      return (
        <TutorialBox title={title} help={properties.help}>
          <div className="text-sm leading-relaxed text-cede-text-primary">{properties.children}</div>
        </TutorialBox>
      );
    }
    case "info": {
      return (
        <TutorialInfo>
          <div className="text-sm leading-relaxed text-cede-text-secondary">{properties.children}</div>
        </TutorialInfo>
      );
    }
    case "video": {
      return (
        <TutorialVideoCard
          title={renderChildrenAsString(properties.children)}
          onClick={() => redirectInBrowser(properties.url)}
        />
      );
    }
    case "copy": {
      return (
        <div className="mt-2">
          <div
            className="relative bg-cede-bg-popover border border-cede-border-container/40 p-4 rounded-lg text-sm"
            data-testid="copy-box"
          >
            <div className="absolute right-2 top-2">
              <CopyIconButton testId="CopyIconButton" size="medium" text={properties.value} />
            </div>
            {renderChildren(properties.children)}
          </div>
        </div>
      );
    }
    case "affiliation": {
      return <TutorialAffiliation {...(properties as Any)} />;
    }
    case "divider": {
      return <hr className="my-4 border-cede-border-container" />;
    }
    case "withBackground": {
      return (
        <div className="mt-3 space-y-2 rounded-medium border border-cede-border-container/40 bg-cede-bg-component/40 px-3 py-2 text-sm text-cede-text-primary">
          {properties.children}
        </div>
      );
    }
    default: {
      return <div {...properties} />;
    }
  }
}

export function reactMarkdownComponents(
  folder: string,
  permissions: ApiPermissions[],
  accountPermissions: ApiPermissions[],
  exchangeId: string,
  actionProps: ActionProps,
  data: Record<string, string | number> = {},
  requireTwoKeys: boolean,
  onLinkClick?: (url: string, trackedType: string | undefined) => void,
) {
  const cex = exchangeId.toLowerCase();

  return {
    div: ({ node, ...props }: Any) => {
      const componentType = props.type;
      const identifier = props.identifier || "";
      const actions = actionProps[identifier];

      // Used for nested elements to handle the componentType and the properties
      if (!componentType) {
        const properties = (Array.isArray(node.children) ? node.children[0] : node.children) || {};
        const { type, ...rest } = properties;

        return divTransformer(type, { ...rest, ...actions, children: props.children }, permissions);
      }

      return divTransformer(
        componentType,
        {
          ...props,
          ...actions,
        },
        permissions,
      );
    },
    a: (props: Any) => {
      if (data[props.custom]) {
        return (
          <ExternalLink
            label={renderChildrenAsString(props.children)}
            url={String(data[props.custom])}
            onClick={() => onLinkClick?.(String(data[props.custom]), props.tracked)}
          />
        );
      } else {
        return (
          <ExternalLink
            label={renderChildrenAsString(props.children)}
            url={String(props.href)}
            onClick={() => onLinkClick?.(String(props.href), props.tracked)}
          />
        );
      }
    },
    img: (props: Any) => {
      // Check the permissions of the current process to display the image or not
      if (!checkRequiredPermissionStatement(props.require, permissions)) return null;

      return (
        <WrappedExtensionImage
          alt={props.alt}
          path={`tutorials/${folder}/` + props.src.replace("./", "")}
          width={props.width}
          canZoom={props.zoom}
          icon={props.icon}
          iconColor={props.iconcolor}
          type={props["data-type"]}
          data={data}
        />
      );
    },
    strong: (props: Any) => (
      <strong className={cn("inline font-semibold", props.color === "disabled" && "text-cede-text-secondary")}>
        {props.children}
      </strong>
    ),
    h1: (props: Any) => (
      <span
        className={cn("inline text-base font-semibold", props.color === "disabled" && "text-cede-text-secondary")}
        style={props.style || {}}
      >
        {props.children}
      </span>
    ),
    p: (props: Any) => (
      <span
        className={cn("inline text-sm leading-relaxed", props.color === "disabled" && "text-cede-text-secondary")}
        style={props.style || {}}
      >
        {props.children}
      </span>
    ),
    span: (props: Any) => {
      if (props.type === "var") {
        let value = "";
        if (props.name === "permissions") {
          value = permissions.map((perm) => makeFirstLettersCapital(perm)).join("/");
        } else if (props.name === "step") {
          if (requireTwoKeys) {
            // Account does not exist
            if (accountPermissions.length === 0) {
              // If the permissions are proxyfied, we'll have another step (1/2), else it's (1/1)
              value = hasPermissionsWithProxy(permissions, cex) ? "(1/2)" : "(1/1)";
            } else {
              // Account exists
              // Permissions are both proxyfied and whitelisted, so it's the second step
              if (hasPermissionsWithProxy(permissions, cex) && hasPermissionsWithoutProxy(permissions, cex)) {
                value = "(2/2)";
                // We're just updating/adding a single key
              } else {
                value = "(1/1)";
              }
            }
          } else {
            value = "(1/1)";
          }
        } else if (props.name === "publicKey") {
          return <span className="inline text-cede-text-primary">{data[props.name]}</span>;
        } else if (props.name === "address-description") {
          return <AddressDescription name={String(data.name)} address={String(data.address)} />;
        } else if (props.name === "address-verification") {
          switch (data.status) {
            case "waiting":
              return <SpinnerLoader />;
            case "success":
              return <CheckCircle className="w-[30px] h-[30px] text-cede-success" />;
            case "failed":
              return <XCircle className="w-[30px] h-[30px] text-cede-error" />;
            default:
              return <></>;
          }
        } else if (data[props.name]) {
          return <span className="inline text-cede-primary">{data[props.name]}</span>;
        }

        if (!value) return null;
        return <span className="inline">{value}</span>;
      }
      return null;
    },
    ul: (props: Any) => {
      if (props.require && !checkRequiredPermissionStatement(props.require, permissions)) return null;
      return <TutorialList strippable={props.strippable}>{props.children}</TutorialList>;
    },
    ol: (props: Any) => (
      <ol className="ml-4 list-decimal text-sm leading-relaxed text-cede-text-primary">{props.children}</ol>
    ),
    i: (props: Any) => <span className="italic">{props.children}</span>,
  };
}

export function TutorialReader({
  mdContent,
  currentPage = 0,
  setMaxPage,
  folder,
  permissions,
  accountPermissions,
  exchangeId,
  actionProps = DEFAULT_ACTION_PROPS,
  data = DEFAULT_DATA,
  className,
  ...props
}: TutorialReaderProps) {
  const { supportedExchangesById } = useSupportedExchanges();

  // Open page with helper - Chrome extension specific, for hub-v2 just open the link
  const { mutateAsync: openTabWithHelper } = useMutation({
    mutationFn: async (params: { link: string; exchangeId: string; permissions: ApiPermissions[] }) => {
      window.open(params.link, "_blank");
      return "success";
    },
  });

  const onLinkClick = async (link: string, trackedType: string | undefined) => {
    if (!trackedType) return;

    if (trackedType === "helper") {
      await openTabWithHelper({ link, exchangeId, permissions });
    }
  };

  const pages = useMemo(() => {
    if (!mdContent) return [];
    return extractPages(mdContent, !setMaxPage ? true : false);
  }, [mdContent]);
  // mdContent, folder, exchangeId

  const components = useMemo(() => {
    const mdComponents = reactMarkdownComponents(
      folder,
      permissions,
      accountPermissions,
      exchangeId,
      actionProps,
      data,
      supportedExchangesById[exchangeId]?.requiresTwoKeys ?? false,
      onLinkClick,
    );
    return mdComponents;
  }, [actionProps, data, exchangeId, folder, onLinkClick, permissions, accountPermissions, supportedExchangesById]);

  useEffect(() => {
    setMaxPage?.(pages.length - 1);
  }, [pages]);

  return (
    <div
      className={cn("mt-4 flex h-full flex-col gap-5 text-sm leading-relaxed text-cede-text-primary", className)}
      {...props}
    >
      <ReactMarkdown
        rehypePlugins={REHYPE_PLUGINS}
        components={components}
        children={typeof pages === "object" ? (pages[currentPage] ?? "") : pages || ""}
      />
    </div>
  );
}
